/**
 * Loads a single-image document (PNG/JPEG/WebP/…) and exposes the same
 * `PageRenderer` contract as `usePdfDocument`, so the viewer shell can
 * treat both sources uniformly.
 *
 * Single-image docs have exactly one page. Intrinsic dimensions are the
 * decoded image's pixel size.
 */
import { useEffect, useMemo, useState } from 'react'

import type { PageRenderer, Rotation } from './types'

interface ImageDocState {
  pages: PageRenderer['pages']
  loading: boolean
  error: Error | null
  bitmap: HTMLImageElement | null
}

const INITIAL: ImageDocState = {
  pages: [],
  loading: true,
  error: null,
  bitmap: null,
}

export function useImageDocument(url: string | null): PageRenderer {
  const [state, setState] = useState<ImageDocState>(INITIAL)

  // Reset to "loading" synchronously when the URL changes — done during
  // render to avoid synchronous setState inside the loader effect.
  const [prevUrl, setPrevUrl] = useState<string | null>(url)
  if (prevUrl !== url) {
    setPrevUrl(url)
    setState(url ? { pages: [], loading: true, error: null, bitmap: null } : INITIAL)
  }

  useEffect(() => {
    if (!url) return

    let cancelled = false

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'

    img.onload = () => {
      if (cancelled) return
      setState({
        pages: [
          {
            id: 'image-page-1',
            index: 0,
            pageNumber: 1,
            intrinsic: {
              width: img.naturalWidth,
              height: img.naturalHeight,
            },
          },
        ],
        loading: false,
        error: null,
        bitmap: img,
      })
    }
    img.onerror = () => {
      if (cancelled) return
      setState({
        pages: [],
        loading: false,
        error: new Error('Failed to load image'),
        bitmap: null,
      })
    }
    img.src = url

    return () => {
      cancelled = true
    }
  }, [url])

  return useMemo<PageRenderer>(() => {
    const renderPage: PageRenderer['renderPage'] = async ({
      canvas,
      scale,
      rotation,
      devicePixelRatio,
    }) => {
      const bitmap = state.bitmap
      if (!bitmap) throw new Error('Image is not loaded')

      const rotated = rotation === 90 || rotation === 270
      const intrinsicW = bitmap.naturalWidth
      const intrinsicH = bitmap.naturalHeight
      const cssW = (rotated ? intrinsicH : intrinsicW) * scale
      const cssH = (rotated ? intrinsicW : intrinsicH) * scale

      canvas.width = Math.max(1, Math.floor(cssW * devicePixelRatio))
      canvas.height = Math.max(1, Math.floor(cssH * devicePixelRatio))
      canvas.style.width = `${Math.floor(cssW)}px`
      canvas.style.height = `${Math.floor(cssH)}px`

      const ctx = canvas.getContext('2d', { alpha: false })
      if (!ctx) throw new Error('Canvas 2D context unavailable')

      // Match the canvas bitmap scale so the image is drawn at the
      // intended device-pixel density.
      ctx.save()
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.scale(scale * devicePixelRatio, scale * devicePixelRatio)
      ctx.translate(
        (rotated ? intrinsicH : intrinsicW) / 2,
        (rotated ? intrinsicW : intrinsicH) / 2,
      )
      ctx.rotate(((rotation as Rotation) * Math.PI) / 180)
      ctx.drawImage(bitmap, -intrinsicW / 2, -intrinsicH / 2)
      ctx.restore()
    }

    return {
      pages: state.pages,
      loading: state.loading,
      error: state.error,
      renderPage,
    }
  }, [state.pages, state.loading, state.error, state.bitmap])
}
